import { useTranslation } from"react-i18next";
import { useState, useEffect } from"react";
import axios from"axios";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Input } from"@/components/ui/input";
import { Label } from"@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from"@/components/ui/dialog";
import { Badge } from"@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { DollarSign, TrendingUp, CreditCard, AlertCircle, Eye, Calendar, Edit, Trash2, Plus, Bell, CheckCircle, FileText, Download, ExternalLink } from"lucide-react";
import { toast } from"sonner";

const FinancesPage = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [salesData, setSalesData] = useState([]);
 const [summary, setSummary] = useState({
 total_sales_count: 0,
 total_contract_value: 0,
 total_down_payments: 0,
 total_paid_from_schedules: 0,
 total_outstanding: 0,
 paid_count: 0
 });
 const [loading, setLoading] = useState(true);
 const [selectedSale, setSelectedSale] = useState(null);
 const [detailOpen, setDetailOpen] = useState(false);
 const [editMode, setEditMode] = useState(false);
 const [upcomingPayments, setUpcomingPayments] = useState([]);
 const [editFormData, setEditFormData] = useState({
 contract_value:"",
 deposit_amount:"",
 payment_plan: []
 });

 useEffect(() => {
 loadFinancialData();
 }, []);

 const loadFinancialData = async () => {
 try {
 setLoading(true);
 const response = await axios.get("/finances/sales");
 const sales = response.data.sales || [];
 
 // Calculate paid count and enhance sales with payment status
 let paidCount = 0;
 const enhancedSales = sales.map(sale => {
 const isPaid = sale.outstanding_amount === 0 || sale.outstanding_amount <= 0;
 if (isPaid) paidCount++;
 return {
 ...sale,
 payment_status: isPaid ? 'paid' : 'pending',
 next_payment_due: getNextPaymentDue(sale.payment_plan)
 };
 });
 
 // Sort by next payment due date (soonest first), paid items at the end
 enhancedSales.sort((a, b) => {
 // Paid sales go to the end
 if (a.payment_status === 'paid' && b.payment_status !== 'paid') return 1;
 if (b.payment_status === 'paid' && a.payment_status !== 'paid') return -1;
 
 // Sort by next payment due date
 if (!a.next_payment_due && !b.next_payment_due) return 0;
 if (!a.next_payment_due) return 1;
 if (!b.next_payment_due) return -1;
 return new Date(a.next_payment_due) - new Date(b.next_payment_due);
 });
 
 // Calculate upcoming payments notifications (7 days, 3 days, today)
 const upcoming = calculateUpcomingPayments(enhancedSales);
 setUpcomingPayments(upcoming);
 
 setSummary({
 ...response.data.summary,
 paid_count: paidCount
 });
 setSalesData(enhancedSales);
 } catch (error) {
 toast.error(error.response?.data?.detail || t('messages.loadFailed'));
 } finally {
 setLoading(false);
 }
 };

 // Get the next unpaid payment due date
 const getNextPaymentDue = (paymentPlan) => {
 if (!paymentPlan || paymentPlan.length === 0) return null;
 const unpaidPayments = paymentPlan.filter(p => p.status !== 'PAID');
 if (unpaidPayments.length === 0) return null;
 
 // Sort by due date and return the earliest
 unpaidPayments.sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
 return unpaidPayments[0].due_date;
 };

 // Calculate upcoming payment notifications
 const calculateUpcomingPayments = (sales) => {
 const today = new Date();
 today.setHours(0, 0, 0, 0);
 const notifications = [];
 
 sales.forEach(sale => {
 if (sale.payment_status === 'paid') return;
 if (!sale.payment_plan || sale.payment_plan.length === 0) return;
 
 sale.payment_plan.forEach(payment => {
 if (payment.status === 'PAID') return;
 
 const dueDate = new Date(payment.due_date);
 dueDate.setHours(0, 0, 0, 0);
 const diffTime = dueDate - today;
 const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
 
 if (diffDays === 0) {
 notifications.push({
 type: 'today',
 sale,
 payment,
 message: t('finances.paymentDueToday', { amount: payment.amount?.toLocaleString() }),
 buyer: sale.buyer_name,
 unit: sale.unit_code
 });
 } else if (diffDays === 3) {
 notifications.push({
 type: '3days',
 sale,
 payment,
 message: t('finances.paymentDueIn3Days', { amount: payment.amount?.toLocaleString() }),
 buyer: sale.buyer_name,
 unit: sale.unit_code
 });
 } else if (diffDays === 7) {
 notifications.push({
 type: '7days',
 sale,
 payment,
 message: t('finances.paymentDueIn7Days', { amount: payment.amount?.toLocaleString() }),
 buyer: sale.buyer_name,
 unit: sale.unit_code
 });
 } else if (diffDays < 0) {
 notifications.push({
 type: 'overdue',
 sale,
 payment,
 message: t('finances.paymentOverdue', { amount: payment.amount?.toLocaleString(), days: Math.abs(diffDays) }),
 buyer: sale.buyer_name,
 unit: sale.unit_code
 });
 }
 });
 });
 
 // Sort: overdue first, then today, then 3 days, then 7 days
 const priority = { overdue: 0, today: 1, '3days': 2, '7days': 3 };
 notifications.sort((a, b) => priority[a.type] - priority[b.type]);
 
 return notifications;
 };

 const formatCurrency = (amount) => {
 return new Intl.NumberFormat('en-US', {
 style: 'currency',
 currency: 'EUR',
 minimumFractionDigits: 0,
 maximumFractionDigits: 0
 }).format(amount || 0);
 };

 const formatDate = (dateString) => {
 if (!dateString) return"N/A";
 return new Date(dateString).toLocaleDateString('en-GB', {
 day: '2-digit',
 month: 'short',
 year: 'numeric'
 });
 };

 const openSaleDetail = (sale) => {
 setSelectedSale(sale);
 setEditMode(false);
 setDetailOpen(true);
 };

 const startEdit = () => {
 setEditFormData({
 contract_value: selectedSale.contract_value ||"",
 deposit_amount: selectedSale.down_payment ||"",
 payment_plan: selectedSale.payment_plan || []
 });
 setEditMode(true);
 };

 const cancelEdit = () => {
 setEditMode(false);
 setEditFormData({
 contract_value:"",
 deposit_amount:"",
 payment_plan: []
 });
 };

 const addPaymentRow = () => {
 setEditFormData({
 ...editFormData,
 payment_plan: [
 ...editFormData.payment_plan,
 {
 amount: 0,
 due_date: new Date().toISOString().split('T')[0],
 status:"PLANNED"
 }
 ]
 });
 };

 const removePaymentRow = (index) => {
 const newPaymentPlan = editFormData.payment_plan.filter((_, i) => i !== index);
 setEditFormData({ ...editFormData, payment_plan: newPaymentPlan });
 };

 const updatePaymentRow = (index, field, value) => {
 const newPaymentPlan = [...editFormData.payment_plan];
 newPaymentPlan[index] = { ...newPaymentPlan[index], [field]: value };
 setEditFormData({ ...editFormData, payment_plan: newPaymentPlan });
 };

 const saveChanges = async () => {
 try {
 // Prepare update data
 const updateData = {
 contract_value: parseFloat(editFormData.contract_value),
 deposit_amount: parseFloat(editFormData.deposit_amount),
 payment_plan: editFormData.payment_plan.map(item => ({
 amount: parseFloat(item.amount),
 due_date: new Date(item.due_date).toISOString(),
 status: item.status
 }))
 };

 await axios.patch(`/reservations/${selectedSale.id}`, updateData);
 toast.success(t("messages.updateSuccess"));
 setEditMode(false);
 setDetailOpen(false);
 loadFinancialData(); // Reload data
 } catch (error) {
 toast.error(error.response?.data?.detail || t("messages.saveFailed"));
 }
 };

 if (loading) {
 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="flex items-center justify-center h-64">
 <p className="text-slate-500">{t('finances.loadingFinancialData')}</p>
 </div>
 </Layout>
 );
 }

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="p-6 space-y-6">
 {/* Page Header */}
 <div className="flex items-center justify-between">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900">
 {t('finances.title')}
 </h1>
 <p className="text-slate-500 mt-1">{t('finances.subtitle')}</p>
 </div>
 </div>

 {/* Payment Notifications */}
 {upcomingPayments.length > 0 && (
 <Card className="">
 <CardHeader className="pb-2">
 <CardTitle className="flex items-center gap-2 text-slate-900">
 <Bell className="h-6 w-6 text-amber-600"/>
 {t('finances.paymentNotifications')} ({upcomingPayments.length})
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="space-y-2">
 {upcomingPayments.slice(0, 5).map((notification, idx) => (
 <div 
 key={idx} 
 className="flex items-center justify-between p-3 rounded-sm bg-white border border-slate-200"
 >
 <div>
 <p className="font-medium text-slate-900">
 {notification.message}
 </p>
 <p className="text-sm text-slate-600">
 {notification.buyer} • {t('finances.unit')}: {notification.unit}
 </p>
 </div>
 <Badge className={
 notification.type === 'overdue' ? 'bg-rose-500/10 text-rose-700' :
 notification.type === 'today' ? 'bg-amber-500/10 text-amber-700' :
 notification.type === '3days' ? 'bg-amber-500/10 text-amber-700' :
 'bg-blue-500/10 text-blue-700'
 }>
 {notification.type === 'overdue' ? t('finances.overdue') :
 notification.type === 'today' ? t('finances.todayBadge') :
 notification.type === '3days' ? t('finances.threeDays') : t('finances.sevenDays')}
 </Badge>
 </div>
 ))}
 {upcomingPayments.length > 5 && (
 <p className="text-sm text-slate-500 text-center pt-2">
 + {upcomingPayments.length - 5} {t('finances.moreNotifications', { count: upcomingPayments.length - 5 })}
 </p>
 )}
 </div>
 </CardContent>
 </Card>
 )}

 {/* Summary Cards */}
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
 <Card className="">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-xs font-medium text-slate-500">{t('finances.totalSalesCount')}</p>
 <p className="text-2xl font-bold text-slate-900 mt-1">{summary.total_sales_count}</p>
 </div>
 <DollarSign className="h-7 w-7 text-blue-600"/>
 </div>
 </CardContent>
 </Card>

 <Card className="">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-xs font-medium text-slate-500">{t('finances.fullyPaid')}</p>
 <p className="text-2xl font-bold text-slate-900 mt-1">{summary.paid_count || 0}</p>
 </div>
 <CheckCircle className="h-7 w-7 text-emerald-600"/>
 </div>
 </CardContent>
 </Card>

 <Card className="">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-xs font-medium text-slate-500">{t('finances.contractValue')}</p>
 <p className="text-2xl font-bold text-slate-900 mt-1">{formatCurrency(summary.total_contract_value)}</p>
 </div>
 <TrendingUp className="h-7 w-7 text-emerald-600"/>
 </div>
 </CardContent>
 </Card>

 <Card className="">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-xs font-medium text-slate-500">{t('finances.downPayments')}</p>
 <p className="text-2xl font-bold text-slate-900 mt-1">{formatCurrency(summary.total_down_payments)}</p>
 </div>
 <CreditCard className="h-7 w-7 text-violet-600"/>
 </div>
 </CardContent>
 </Card>

 <Card className="">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-xs font-medium text-slate-500">{t('finances.paidSchedule')}</p>
 <p className="text-2xl font-bold text-slate-900 mt-1">{formatCurrency(summary.total_paid_from_schedules)}</p>
 </div>
 <CreditCard className="h-7 w-7 text-sky-600"/>
 </div>
 </CardContent>
 </Card>

 <Card className="">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-xs font-medium text-slate-500">{t('finances.outstanding')}</p>
 <p className="text-2xl font-bold text-slate-900 mt-1">{formatCurrency(summary.total_outstanding)}</p>
 </div>
 <AlertCircle className="h-7 w-7 text-amber-600"/>
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Sales Table - Pending Payments */}
 <Card>
 <CardHeader>
 <CardTitle>{t('finances.sales')}</CardTitle>
 </CardHeader>
 <CardContent>
 {salesData.filter(s => s.payment_status !== 'paid').length === 0 ? (
 <div className="text-center py-12">
 <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-3"/>
 <p className="text-slate-500">{t('finances.noPendingSales')}</p>
 </div>
 ) : (
 <div className="overflow-x-auto">
 <table className="w-full">
 <thead className="bg-white/50 border-b">
 <tr>
 <th className="text-left py-3 px-4 text-sm font-semibold text-[#334155]">{t('finances.buyer')}</th>
 <th className="text-left py-3 px-4 text-sm font-semibold text-[#334155]">{t('finances.unit')}</th>
 <th className="text-left py-3 px-4 text-sm font-semibold text-[#334155]">{t('finances.project')}</th>
 <th className="text-right py-3 px-4 text-sm font-semibold text-[#334155]">{t('finances.contractValue')}</th>
 <th className="text-right py-3 px-4 text-sm font-semibold text-[#334155]">{t('finances.outstanding')}</th>
 <th className="text-left py-3 px-4 text-sm font-semibold text-[#334155]">{t('finances.nextPayment')}</th>
 <th className="text-center py-3 px-4 text-sm font-semibold text-[#334155]">{t('common.actions')}</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-slate-100">
 {salesData.filter(s => s.payment_status !== 'paid').map((sale) => {
 const nextDueDate = sale.next_payment_due ? new Date(sale.next_payment_due) : null;
 const today = new Date();
 today.setHours(0, 0, 0, 0);
 const daysUntilDue = nextDueDate ? Math.ceil((nextDueDate - today) / (1000 * 60 * 60 * 24)) : null;
 const isOverdue = daysUntilDue !== null && daysUntilDue < 0;
 
 return (
 <tr key={sale.id} className="transition-colors hover:bg-white/80">
 <td className="py-3 px-4">
 <div>
 <p className="font-medium text-slate-900">{sale.buyer_name}</p>
 <p className="text-sm text-slate-500">{sale.buyer_email}</p>
 </div>
 </td>
 <td className="py-3 px-4">
 <div>
 <p className="font-medium text-slate-900">{sale.unit_code}</p>
 <p className="text-sm text-slate-500">{sale.building_name}</p>
 </div>
 </td>
 <td className="py-3 px-4">
 <p className="text-slate-900">{sale.project_name}</p>
 </td>
 <td className="py-3 px-4 text-right">
 <p className="font-semibold text-slate-900">{formatCurrency(sale.contract_value)}</p>
 </td>
 <td className="py-3 px-4 text-right">
 <p className="font-medium text-slate-900">{formatCurrency(sale.outstanding_amount)}</p>
 </td>
 <td className="py-3 px-4">
 {nextDueDate ? (
 <div>
 <p className="text-sm font-medium text-slate-600">
 {formatDate(sale.next_payment_due)}
 </p>
 {daysUntilDue !== null && (
 <div className="text-xs font-medium">
 {isOverdue ? (
 <Badge className="bg-rose-500/10 text-rose-700 text-xs">{t('finances.daysOverdue', { count: Math.abs(daysUntilDue) })}</Badge>
 ) : daysUntilDue === 0 ? (
 <Badge className="bg-amber-500/10 text-amber-700 text-xs">{t('finances.dueToday')}</Badge>
 ) : daysUntilDue <= 7 ? (
 <Badge className="bg-amber-500/10 text-amber-700 text-xs">{t('finances.daysLeft', { count: daysUntilDue })}</Badge>
 ) : (
 <span className="text-slate-400">{t('finances.daysLeft', { count: daysUntilDue })}</span>
 )}
 </div>
 )}
 </div>
 ) : (
 <span className="text-slate-400 text-sm">{t('finances.noSchedule')}</span>
 )}
 </td>
 <td className="py-3 px-4 text-center">
 <Button
 size="sm"
 variant="outline"
 onClick={() => openSaleDetail(sale)}
 >
 <Eye className="w-4 h-4 mr-1"/>
 {t("finances.view")}
 </Button>
 </td>
 </tr>
 );
 })}
 </tbody>
 </table>
 </div>
 )}
 </CardContent>
 </Card>

 {/* Fully Paid Sales */}
 {salesData.filter(s => s.payment_status === 'paid').length > 0 && (
 <Card className="">
 <CardHeader className="border-b border-white/40">
 <CardTitle className="flex items-center gap-2 text-slate-900">
 <CheckCircle className="h-6 w-6 text-emerald-600"/>
 {t('finances.fullyPaid')}
 </CardTitle>
 </CardHeader>
 <CardContent className="pt-4">
 <div className="overflow-x-auto">
 <table className="w-full">
 <thead className="bg-white/50 border-b border-slate-200">
 <tr>
 <th className="text-left py-3 px-4 text-sm font-semibold text-[#334155]">{t('finances.buyer')}</th>
 <th className="text-left py-3 px-4 text-sm font-semibold text-[#334155]">{t('finances.unit')}</th>
 <th className="text-left py-3 px-4 text-sm font-semibold text-[#334155]">{t('finances.project')}</th>
 <th className="text-right py-3 px-4 text-sm font-semibold text-[#334155]">{t('finances.contractValue')}</th>
 <th className="text-left py-3 px-4 text-sm font-semibold text-[#334155]">{t('finances.paidDate')}</th>
 <th className="text-center py-3 px-4 text-sm font-semibold text-[#334155]">{t('common.actions')}</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-gray-100">
 {salesData.filter(s => s.payment_status === 'paid').map((sale) => (
 <tr key={sale.id} className="hover:bg-white/80 transition-colors">
 <td className="py-3 px-4">
 <div>
 <p className="font-medium text-slate-900">{sale.buyer_name}</p>
 <p className="text-sm text-slate-500">{sale.buyer_email}</p>
 </div>
 </td>
 <td className="py-3 px-4">
 <div>
 <p className="font-medium text-slate-900">{sale.unit_code}</p>
 <p className="text-sm text-slate-500">{sale.building_name}</p>
 </div>
 </td>
 <td className="py-3 px-4">
 <p className="text-slate-900">{sale.project_name}</p>
 </td>
 <td className="py-3 px-4 text-right">
 <p className="font-semibold text-slate-900">{formatCurrency(sale.contract_value)}</p>
 </td>
 <td className="py-3 px-4">
 <p className="text-sm text-slate-500">{formatDate(sale.confirmed_at)}</p>
 </td>
 <td className="py-3 px-4 text-center">
 <Button
 size="sm"
 variant="outline"
 onClick={() => openSaleDetail(sale)}
 >
 <Eye className="w-4 h-4 mr-1"/>
 {t("finances.view")}
 </Button>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </CardContent>
 </Card>
 )}
 </div>

 {/* Sale Detail Modal */}
 <Dialog open={detailOpen} onOpenChange={(open) => { setDetailOpen(open); if (!open) setEditMode(false); }}>
 <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
 <DialogHeader>
 <DialogTitle>{editMode ? t('finances.editSale') : t('finances.saleDetails')}</DialogTitle>
 </DialogHeader>
 {selectedSale && !editMode && (
 <div className="space-y-6">
 {/* Buyer Information */}
 <div className="bg-white/50 p-4 rounded-sm border border-slate-200">
 <h3 className="font-semibold text-slate-900 mb-3">{t('finances.buyerInformation')}</h3>
 <div className="grid grid-cols-2 gap-3 text-sm">
 <div>
 <p className="text-slate-600">{t('finances.name')}</p>
 <p className="font-medium text-slate-900">{selectedSale.buyer_name}</p>
 </div>
 <div>
 <p className="text-slate-600">{t('finances.email')}</p>
 <p className="font-medium text-slate-900">{selectedSale.buyer_email}</p>
 </div>
 <div>
 <p className="text-slate-600">{t('finances.phone')}</p>
 <p className="font-medium text-slate-900">{selectedSale.buyer_phone}</p>
 </div>
 </div>
 </div>

 {/* Property Information */}
 <div className="bg-white/50 p-4 rounded-sm border border-slate-200">
 <h3 className="font-semibold text-slate-900 mb-3">{t('finances.propertyInformation')}</h3>
 <div className="grid grid-cols-2 gap-3 text-sm">
 <div>
 <p className="text-slate-600">{t('finances.project')}</p>
 <p className="font-medium text-slate-900">{selectedSale.project_name}</p>
 </div>
 <div>
 <p className="text-slate-600">{t('finances.building')}</p>
 <p className="font-medium text-slate-900">{selectedSale.building_name}</p>
 </div>
 <div>
 <p className="text-slate-600">{t('finances.unitCode')}</p>
 <p className="font-medium text-slate-900">{selectedSale.unit_code}</p>
 </div>
 </div>
 </div>

 {/* Financial Details */}
 <div className="bg-white/50 p-4 rounded-sm border border-slate-200">
 <h3 className="font-semibold text-slate-900 mb-3">{t('finances.financialDetails')}</h3>
 <div className="grid grid-cols-2 gap-4 text-sm">
 <div className="bg-white p-3 rounded border border-slate-200">
 <p className="text-slate-600 mb-1">{t('finances.contractValue')}</p>
 <p className="text-2xl font-bold text-slate-900">{formatCurrency(selectedSale.contract_value)}</p>
 </div>
 <div className="bg-white p-3 rounded border border-slate-200">
 <p className="text-slate-600 mb-1">{t('finances.downPayment')}</p>
 <p className="text-2xl font-bold text-slate-900">{formatCurrency(selectedSale.down_payment)}</p>
 </div>
 <div className="bg-white p-3 rounded border border-slate-200">
 <p className="text-slate-600 mb-1">{t('finances.outstandingAmount')}</p>
 <p className="text-2xl font-bold text-slate-900">{formatCurrency(selectedSale.outstanding_amount)}</p>
 </div>
 <div className="bg-white p-3 rounded border border-slate-200">
 <p className="text-slate-600 mb-1">{t('finances.paymentMethod')}</p>
 <p className="text-lg font-medium text-slate-900 capitalize">{selectedSale.payment_method}</p>
 </div>
 </div>
 </div>

 {/* Payment Schedule */}
 {selectedSale.payment_plan && selectedSale.payment_plan.length > 0 && (
 <div className="bg-white/50 p-4 rounded-sm border border-slate-200">
 <h3 className="font-semibold text-slate-900 mb-3 flex items-center">
 <Calendar className="w-4 h-4 mr-2"/>
 {t('finances.paymentSchedule')}
 </h3>
 <div className="space-y-2">
 {selectedSale.payment_plan.map((payment, index) => (
 <div key={index} className="bg-white p-3 rounded border flex items-center justify-between">
 <div className="flex items-center gap-3">
 <Badge variant={
 payment.status ==="PAID"?"success":
 payment.status ==="OVERDUE"?"destructive":"secondary"
 }>
 {payment.status}
 </Badge>
 <div>
 <p className="font-medium text-slate-900">{formatCurrency(payment.amount)}</p>
 <p className="text-sm text-slate-500">
 Due: {formatDate(payment.due_date)}
 </p>
 </div>
 </div>
 </div>
 ))}
 </div>
 <div className="mt-4 pt-3 border-t grid grid-cols-2 gap-3 text-sm">
 <div>
 <p className="text-slate-600">{t('finances.totalPaid')}</p>
 <p className="font-semibold text-slate-900">{formatCurrency(selectedSale.total_paid)}</p>
 </div>
 <div>
 <p className="text-slate-600">{t('finances.totalPlanned')}</p>
 <p className="font-semibold text-[#334155]">{formatCurrency(selectedSale.total_planned)}</p>
 </div>
 </div>
 </div>
 )}

 {/* Contract Document */}
 {selectedSale.contract_document_url && (
 <div className="bg-white/50 p-4 rounded-sm border border-slate-200">
 <h3 className="font-semibold text-slate-900 mb-3 flex items-center">
 <FileText className="w-4 h-4 mr-2"/>
 {t('finances.contractDocument')}
 </h3>
 <div className="flex items-center gap-4">
 <div className="flex-1">
 <p className="text-sm text-slate-600">
 {selectedSale.contract_document_url.includes('/uploads/') 
 ? t('finances.uploadedContract')
 : t('finances.externalLink')}
 </p>
 </div>
 <div className="flex gap-2">
 <a
 href={selectedSale.contract_document_url.startsWith('/api') 
 ? selectedSale.contract_document_url 
 : selectedSale.contract_document_url}
 target="_blank"
 rel="noopener noreferrer"
 className="inline-flex items-center px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors"
 >
 <Eye className="w-4 h-4 mr-2"/>
 {t('finances.view')}
 </a>
 <a
 href={selectedSale.contract_document_url.startsWith('/api') 
 ? selectedSale.contract_document_url 
 : selectedSale.contract_document_url}
 download
 className="inline-flex items-center px-4 py-2 bg-slate-600 text-white rounded-md hover:bg-slate-700 transition-colors"
 >
 <Download className="w-4 h-4 mr-2"/>
 {t('finances.download')}
 </a>
 </div>
 </div>
 </div>
 )}

 {/* Action Buttons */}
 <div className="flex gap-3 pt-4 border-t">
 <Button
 onClick={startEdit}
 className="flex-1 bg-slate-900 hover:bg-slate-800:bg-zinc-200"
 >
 <Edit className="w-4 h-4 mr-2"/>
 {t('finances.editSale')}
 </Button>
 <Button
 variant="outline"
 onClick={() => setDetailOpen(false)}
 className="flex-1"
 >
 {t('common.close')}
 </Button>
 </div>
 </div>
 )}

 {/* Edit Form */}
 {selectedSale && editMode && (
 <div className="space-y-6">
 {/* Buyer & Property Info (Read-only) */}
 <div className="bg-white/50 p-4 rounded-sm border">
 <div className="grid grid-cols-2 gap-3 text-sm">
 <div>
 <p className="text-slate-600">{t('finances.buyer')}</p>
 <p className="font-medium">{selectedSale.buyer_name}</p>
 </div>
 <div>
 <p className="text-slate-600">{t('finances.unit')}</p>
 <p className="font-medium">{selectedSale.unit_code} - {selectedSale.project_name}</p>
 </div>
 </div>
 </div>

 {/* Editable Financial Details */}
 <div className="space-y-4">
 <h3 className="font-semibold text-slate-900">{t('finances.financialDetails')}</h3>
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label htmlFor="edit-contract-value">{t('finances.contractValue')} (€)</Label>
 <Input
 id="edit-contract-value"
 type="number"
 value={editFormData.contract_value}
 onChange={(e) => setEditFormData({ ...editFormData, contract_value: e.target.value })}
 placeholder="150000"
 />
 </div>
 <div>
 <Label htmlFor="edit-down-payment">{t('finances.downPayment')} (€)</Label>
 <Input
 id="edit-down-payment"
 type="number"
 value={editFormData.deposit_amount}
 onChange={(e) => setEditFormData({ ...editFormData, deposit_amount: e.target.value })}
 placeholder="5000"
 />
 </div>
 </div>
 </div>

 {/* Editable Payment Schedule */}
 <div className="space-y-4">
 <div className="flex items-center justify-between">
 <h3 className="font-semibold text-slate-900">{t('finances.paymentSchedule')}</h3>
 <Button
 type="button"
 size="sm"
 onClick={addPaymentRow}
 variant="outline"
 >
 <Plus className="w-4 h-4 mr-1"/>
 {t('finances.addPayment')}
 </Button>
 </div>
 
 {editFormData.payment_plan.map((payment, index) => (
 <div key={index} className="bg-white/50 p-3 rounded border">
 <div className="grid grid-cols-4 gap-3">
 <div>
 <Label>{t('finances.paymentAmount')} (€)</Label>
 <Input
 type="number"
 value={payment.amount}
 onChange={(e) => updatePaymentRow(index, 'amount', e.target.value)}
 />
 </div>
 <div>
 <Label>{t('finances.dueDate')}</Label>
 <Input
 type="date"
 value={typeof payment.due_date === 'string' && payment.due_date.includes('T') 
 ? payment.due_date.split('T')[0] 
 : payment.due_date}
 onChange={(e) => updatePaymentRow(index, 'due_date', e.target.value)}
 />
 </div>
 <div>
 <Label>{t('common.status')}</Label>
 <Select
 value={payment.status}
 onValueChange={(value) => updatePaymentRow(index, 'status', value)}
 >
 <SelectTrigger>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="PLANNED">{t('finances.planned')}</SelectItem>
 <SelectItem value="PAID">{t('finances.paid')}</SelectItem>
 <SelectItem value="OVERDUE">{t('finances.overdue')}</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div className="flex items-end">
 <Button
 type="button"
 size="sm"
 variant="destructive"
 onClick={() => removePaymentRow(index)}
 className="w-full"
 >
 <Trash2 className="w-4 h-4"/>
 </Button>
 </div>
 </div>
 </div>
 ))}
 </div>

 {/* Action Buttons */}
 <div className="flex gap-3 pt-4 border-t">
 <Button
 onClick={saveChanges}
 className="flex-1 bg-green-600 hover:bg-green-700"
 >
 {t('finances.saveChanges')}
 </Button>
 <Button
 variant="outline"
 onClick={cancelEdit}
 className="flex-1"
 >
 {t('common.cancel')}
 </Button>
 </div>
 </div>
 )}
 </DialogContent>
 </Dialog>
 </Layout>
 );
};

export default FinancesPage;
